import { Component, OnDestroy, OnInit } from '@angular/core';
import { EstablishmentService } from '../../establishment.service';
import { scaleIn400ms } from '../../../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../../../@vex/animations/scale-fade-in.animation';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import { SupplierModel } from '../../../../../../core/models/supplier.model';
import { EstablishmentCreateUpdateDeleteComponent } from '../../modals/establishment-create-update-delete/establishment-create-update-delete.component';
import { EstablishmentMainEstablishmentUpdateComponent } from '../../modals/establishment-main-establishment-update/establishment-main-establishment-update.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { FeiColumn } from '../../../../../../core/base/interfaces/fei-column.interface';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { userProfileValidatorLevelTransalation } from '../../../../../../core/enums/user-profile-validator-seuil.enum';
import { AuthService } from '../../../../../../core/services/auth.service';
import { EstablishmentCustomerModel, EstablishmentModel } from '../../../../../../core/models/establishment.model';
import { EstablishmentTypeEnum } from '../../../../../../core/enums/establishment-type.enum';
import { EstablishmentCustomerCreateUpdateDeleteComponent } from '../../modals/establishment-customer-create-update-delete/establishment-customer-create-update-delete.component';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { AnnualChargeModel } from '../../../../../../core/models/annual-charge.model';
import { EstablishmentAnnualChargeCreateUpdateComponent } from '../../modals/establishment-annual-charge-create-update/establishment-annual-charge-create-update.component';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'vex-establishment-details-resume',
  templateUrl: './establishment-details-resume.component.html',
  styleUrls: ['./establishment-details-resume.component.scss'],
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
})
export class EstablishmentDetailsResumeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  EstablishmentTypeEnum = EstablishmentTypeEnum;
  translateValidatorMatriceLevel = '';

  establishment: EstablishmentModel;
  canUpdatePermission = false;

  dataSourceEstablishmentCustomers = new MatTableDataSource<EstablishmentCustomerModel>();
  countEstablishmentCustomers = 0;
  sortFielEstablishmentCustomers = 'label';
  sortOrderEstablishmentCustomers = 'asc';
  pageSizeEstablishmentCustomers = 10;
  isCurrentUserPredik = false;

  feisColumnsEstablishmentCustomers: Array<FeiColumn> = [
    { label: 'Id technique', column: 'technicalIdentifier', width: '25%' },
    { label: 'Libellé', column: 'label', width: '65%' },
    {
      label: '',
      column: 'actions',
      width: '10%',
      canDelete: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_ESTABLISHMENT),
      canDeleteDynamically: (row: any) => this.canDeleteEstablishmentCustomer(row),
      canUpdate: false,
    },
  ];

  feisColumnsAnnualCharges: Array<FeiColumn> = [
    { label: 'Année', column: 'year' },
    { label: 'Montant (€)', column: 'amount' },
    { label: '', column: 'actions', canUpdate: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_ESTABLISHMENT) },
  ];
  displayedColumnsAnnualCharges = [];
  dataSourceAnnualCharges = new MatTableDataSource<AnnualChargeModel>();

  loadingData = false;
  ENUM_PERMISSIONS = ENUM_PERMISSIONS;

  constructor(
    public establishmentService: EstablishmentService,
    public dialog: MatDialog,
    public authService: AuthService,
    private snackbarService: SnackbarService,
  ) {
    this.displayedColumnsAnnualCharges = this.feisColumnsAnnualCharges.map(x => x.column);
  }

  canDeleteEstablishmentCustomer(row: any): boolean {
    // L'utilisateur doit avoir la permission ET ce ne doit pas être l'établissement principal
    const hasPermission = this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_ESTABLISHMENT);
    const isNotMainEstablishment = !row?.isMainEstablishment;
    return hasPermission && isNotMainEstablishment;
  }

  ngOnInit() {
    this.initData();
  }

  initData() {
    this.loadingData = true;

    this.establishmentService.entity$
      .pipe(
        takeUntil(this.destroy$),
        filter(entity => !!entity && !!entity._id),
      )
      .subscribe({
        next: (entity: EstablishmentModel) => {
          this.establishment = entity;

          const enrichedCustomers = (entity?.establishmentCustomers || []).map(customer => ({
            ...customer,
            mainEstablishment: entity.mainEstablishmentId && customer.customer_id === entity.mainEstablishmentId,
            isMainEstablishment: entity.mainEstablishmentId && customer.customer_id === entity.mainEstablishmentId,
          }));

          this.dataSourceEstablishmentCustomers = new MatTableDataSource<EstablishmentCustomerModel>(enrichedCustomers);
          this.countEstablishmentCustomers = enrichedCustomers.length;
          this.dataSourceAnnualCharges = new MatTableDataSource<AnnualChargeModel>(entity?.annualCharges ?? []);
          this.loadingData = false;
        },
        error: error => {
          console.error('Error loading establishment data:', error);
          this.loadingData = false;
        },
      });

    this.authService
      .getCurrentUserPermissions$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(permissions => {
        this.canUpdatePermission = permissions.includes(ENUM_PERMISSIONS.UPDATE_ESTABLISHMENT);
      });

    this.isCurrentUserPredik = this.authService.checkCurrentIsPredik();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async updateItem() {
    this.dialog
      .open(EstablishmentCreateUpdateDeleteComponent, {
        data: {
          defaults: await this.establishmentService.entity,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe((supplier: SupplierModel) => {
        if (supplier) {
          // Recharger les données complètes de l'établissement après la mise à jour
          this.establishmentService.findOneById(this.establishment._id).subscribe();
        }
      });
  }

  async addEstablishmentCustomer() {
    const entity = await this.establishmentService.entity;
    this.dialog
      .open(EstablishmentCustomerCreateUpdateDeleteComponent, {
        data: {
          defaults: entity,
          mode: CrudModeEnum.Create,
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.initData();
        }
      });
  }

  async deleteEstablishmentCustomers(element: EstablishmentCustomerModel) {
    const entity = await this.establishmentService.entity;

    // Vérifier si c'est l'établissement principal
    if (entity.mainEstablishmentId && element.customer_id === entity.mainEstablishmentId) {
      this.snackbarService.warning(
        "Impossible de supprimer l'établissement principal de la liste des clients. Il est automatiquement inclus car il s'agit de l'établissement principal de cet établissement support.",
      );
      return;
    }

    this.establishmentService.getImpacts(element).subscribe(res => {
      const impacts = res.data;
      console.log("Impacts de la suppression de l'établissement client", impacts);
      this.dialog
        .open(EstablishmentCustomerCreateUpdateDeleteComponent, {
          data: {
            defaults: entity,
            mode: CrudModeEnum.Delete,
            establishmentCustomer: element,
            dependencies: impacts,
            name: element.label,
          },
        })
        .afterClosed()
        .subscribe(result => {
          if (result) {
            this.initData();
          }
        });
    });
  }

  async updateAnnualCharge(element?: AnnualChargeModel) {
    const entity = await this.establishmentService.entity;
    const mode = element ? CrudModeEnum.Update : CrudModeEnum.Create;
    this.dialog
      .open(EstablishmentAnnualChargeCreateUpdateComponent, {
        data: {
          defaults: element ?? {},
          mode,
          establishmentId: entity._id,
        },
      })
      .afterClosed()
      .subscribe((result: AnnualChargeModel) => {
        if (result) {
          const exists = entity.annualCharges?.some(c => Number(c.year) === Number(result.year) && c !== element);
          if (exists) {
            this.snackbarService.warning(
              `Impossible d\'ajouter cette charge annuelle. Une charge pour cet établissement existe déjà pour l'année ${result.year}.`,
            );
            return;
          }
          this.establishmentService.updateAnnualCharge(entity._id, result).subscribe(() => {
            this.initData();
            this.snackbarService.success(
              `La charge annuelle a bien été ${mode === CrudModeEnum.Update ? 'mise à jour' : 'créée'} !`,
            );
          });
        }
      });
  }

  async updateMainEstablishment() {
    const entity = await this.establishmentService.entity;

    this.dialog
      .open(EstablishmentMainEstablishmentUpdateComponent, {
        data: {
          establishment: entity,
        },
        width: '800px',
        maxWidth: '90vw',
        height: 'auto',
        maxHeight: '90vh',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result: boolean) => {
        if (result) {
          // Recharger les données de l'établissement après modification
          this.initData();
        }
      });
  }
}
